/**
 * Shelves of the whole catalog page (block _collection-groups): a row of products that scrolls
 * sideways. The ‹ › buttons scroll it by the cards in view; they show only when the row does not
 * fit and are disabled at its ends.
 */
class OlinShelf extends HTMLElement {
  /** @type {HTMLElement | null} */
  #row = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {ResizeObserver | null} */
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.#row = this.querySelector('[data-shelf-row]');
    this.#prev = this.querySelector('[data-shelf-prev]');
    this.#next = this.querySelector('[data-shelf-next]');
    if (!this.#row || !this.#prev || !this.#next) return;

    this.#prev.addEventListener('click', this.#onPrev);
    this.#next.addEventListener('click', this.#onNext);
    this.#row.addEventListener('scroll', this.#update, { passive: true });
    this.#observer = new ResizeObserver(this.#update);
    this.#observer.observe(this.#row);
    this.#update();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#row?.removeEventListener('scroll', this.#update);
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #onPrev = () => this.#scroll(-1);
  #onNext = () => this.#scroll(1);

  #update = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const row = this.#row;
      if (!row || !this.#prev || !this.#next) return;

      const end = row.scrollWidth - row.clientWidth;
      this.toggleAttribute('data-scrollable', end > 1);
      this.#prev.disabled = row.scrollLeft <= 1;
      this.#next.disabled = row.scrollLeft >= end - 1;
    });
  };

  /**
   * Scrolls the row by the cards that are fully in view.
   * @param {number} direction - -1 back, 1 forward
   */
  #scroll(direction) {
    const row = this.#row;
    if (!row) return;

    const card = row.firstElementChild;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : row.clientWidth;
    const cards = Math.max(1, Math.floor((row.clientWidth + gap) / step));
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    row.scrollBy({ left: direction * cards * step, behavior: smooth ? 'smooth' : 'auto' });
  }
}

if (!customElements.get('olin-shelf')) customElements.define('olin-shelf', OlinShelf);
